import React, { useState, useEffect } from "react";
import i18n from "i18next";
import { baseUrl } from "../../../helpers/constants";
import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { IDokument } from "../../../Models/IDokument";
import { DocumentColumns } from "./DocumentColumns";
import DownloadNotification from "../../../Controls/DownloadDialog";
import {
  Button,
  Checkbox,
  Input,
  MessageBar,
  OnSelectionChangeData,
  ProgressBar,
  SelectionItemId,
  Tag,
  TagGroup,
} from "@fluentui/react-components";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  ArrowDownload24Regular,
  CheckmarkRegular,
  DismissRegular,
  DocumentRegular,
  FilterRegular,
  SearchRegular,
} from "@fluentui/react-icons";
import LuebPagedDataGrid from "../../../Controls/LuebPagedDataGrid";
import Header from "../../Layout/Header";
import { ILuebOption } from "../../../Models/ILuebOption";

function Documents(props: any) {
  const [searchParam, setSearchParam] = useState<string>("");
  const [newSearchParam, setNewSearchParam] = useState<string>("");
  const [showDownloadNotification, setShowDownloadNotification] =
    useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [documents, setDocuments] = useState<IDokument[]>([]);
  const [selectedDocumentRowIds, setSelectedDocumentRowIds] = useState<
    Set<SelectionItemId>
  >(new Set([]));

  const [showOnlyDeleted, setShowOnlyDeleted] = useState<boolean>(false);
  const getDocuments = () => {
    if (
      searchParam == "" ||
      searchParam.split(",").every((x) => {
        return x.length > 3 || x == "";
      }) ||
      showOnlyDeleted
    ) {
      setLoading(true);

      // Auswahl zurücksetzen
      setSelectedDocumentRowIds(new Set([]));

      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/documents/GetDocumentsByQuery?searchParam=${searchParam}&onlyDeleted=${showOnlyDeleted}`,
        )
        .then((data: any) => {
          setDocuments(data);
        })
        .catch((error) => {})
        .finally(() => {
          setSearched(true);
          setLoading(false);
        });
    } else {
      setDocuments([]);
      setSearched(false);
    }
  };

  const downloadDocumentSet = (selectedDocumentGuids: string[]) => {
    setShowDownloadNotification(true);
    fetchWrapper
      .downloadFileFromPostWithAuth(
        baseUrl + "/api/documents/DownloadDocumentSet",
        selectedDocumentGuids,
      )
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          i18n.language.includes("de")
            ? "Lübbering Digital Dokumente.zip"
            : "Luebbering Digital Documents.zip",
        );

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);

        return true;
      })
      .finally(() => {
        setShowDownloadNotification(false); // Hide the notification after download
      });
  };

  const columns = DocumentColumns(
    searchParam.split(",").filter((x) => x != ""),
    {
      onReloadDocuments: () => {
        getDocuments();
      },
    },
  );

  useEffect(() => {
    getDocuments();
  }, [searchParam, showOnlyDeleted]);
  useEffect(() => {
    getDocuments();
  }, []);

  const selectedDocuments = documents.filter((doc, index) =>
    selectedDocumentRowIds.has(index),
  );

  return (
    <>
      {(columns.showDownloadNotification || showDownloadNotification) && (
        <DownloadNotification />
      )}

      <Header
        HTMLElement={
          <>
            <StackShim horizontal verticalAlign="center">
              <Button
                icon={<DocumentRegular fontSize={24} color="#ff6600" />}
                size="large"
                appearance="subtle"
              >
                {i18n.t<string>("documents").toLocaleUpperCase()}
              </Button>
            </StackShim>
            {columns.deleteExecuteDialog()}
            {columns.changeDeleteExecuteDialog()}
          </>
        }
      />

      <div className="lueb-InnerContentBlockPadding2px">
        <StackShim
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          tokens={{ childrenGap: 15 }}
        >
          <StackShim horizontal tokens={{ childrenGap: 5 }}>
            {/* Such Button */}
            {/* <Button
              appearance="subtle"
              disabled={searchParam.length < 4 && showOnlyDeleted == false}
              icon={<SearchRegular />}
              onClick={() => {
                getDocuments();
              }}
            >
              {i18n.t<string>("search")}
            </Button> */}

            {/* Download Zip Button */}
            <Button
              appearance="subtle"
              disabled={selectedDocuments.length == 0}
              icon={<ArrowDownload24Regular />}
              onClick={() => {
                downloadDocumentSet(
                  selectedDocuments.map((doc) => doc.dokumentGuid),
                );
              }}
            >
              {i18n.t<string>("downloadZip")}
            </Button>

            <Checkbox
              checked={showOnlyDeleted}
              onChange={(ev, data) => {
                setShowOnlyDeleted(data.checked as boolean);
              }}
              /* onClick={() => {
                  getDocuments();
                }} */
              label={i18n.t<string>("onlyDeletedDocuments")}
            />
          </StackShim>
          <Input
            value={newSearchParam}
            onKeyDown={(data) => {
              if (data.key == "Enter" && searchParam.length > 4) {
                setSearchParam(searchParam + newSearchParam + ",");
                setNewSearchParam("");
                getDocuments();
              }
            }}
            placeholder={"Filter (min. 4 Zeichen)"}
            contentAfter={
              newSearchParam != "" ? (
                <Button
                  appearance="transparent"
                  icon={<CheckmarkRegular />}
                  disabled={
                    searchParam.includes(newSearchParam) ||
                    newSearchParam.length < 4
                  }
                  onClick={(ev) => {
                    setSearchParam(searchParam + newSearchParam + ",");
                    setNewSearchParam("");
                  }}
                />
              ) : (
                <FilterRegular />
              )
            }
            onChange={(ev, data) => setNewSearchParam(data.value)}
          />
        </StackShim>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {searchParam.length > 0 && (
          <TagGroup
            onDismiss={(ev, data) => {
              var newParam = searchParam
                .split(",")
                .filter((x) => x != "")
                .filter((x) => x != data.value);

              var tempNewParam =
                newParam.length > 0 ? newParam.join(",") + "," : "";

              setSearchParam(tempNewParam);
            }}
          >
            {searchParam
              .split(",")
              .filter((x) => x != "")
              .map((text) => {
                return (
                  <Tag dismissible value={text} appearance="outline">
                    {text}
                  </Tag>
                );
              })}
          </TagGroup>
        )}
      </div>
      <div className="lueb-ContentBlock">
        <div className="lueb-Padding20">
          <LuebPagedDataGrid
            selectionMode={"multiselect"}
            columns={columns.getColumns()}
            items={loading ? [] : documents}
            itemsOnPage={20}
            resizableColumns={true}
            columnSizingOptions={{
              docFileExtension: { defaultWidth: 60, minWidth: 60 },
              docFileName: { defaultWidth: 270, minWidth: 270 },
              docGroupId: { defaultWidth: 200, minWidth: 150 },
              sprachenId: { defaultWidth: 80, minWidth: 80 },
              artikelId: { defaultWidth: 80, minWidth: 80 },
              auftragId: { defaultWidth: 80, minWidth: 80 },
              systemId: { defaultWidth: 80, minWidth: 80 },
              created: { defaultWidth: 80, minWidth: 80 },
              changeDate: { defaultWidth: 80, minWidth: 80 },
              delBtnId: { defaultWidth: 40, minWidth: 40 },
            }}
            selectedItems={selectedDocumentRowIds}
            onSelectionChange={(e, data: OnSelectionChangeData) => {
              const selItem = data.selectedItems;
              setSelectedDocumentRowIds(selItem);
            }}
            sortable={true}
            /* onSortChange={(e, _sortState) => {
              if (_sortState && _sortState.sortColumn) {
                setSortColumn(_sortState.sortColumn);
              }
            }} */
          />

          {!searched && (
            <MessageBar intent={"info"}>
              {i18n.t<string>("startSearchDocuments")}
            </MessageBar>
          )}
          {searched && documents.length == 0 && (
            <MessageBar intent={"error"}>
              {i18n.t<string>("noItemsFound")}
            </MessageBar>
          )}
          {loading == true && (
            <ProgressBar style={{ marginTop: "5px" }} thickness="large" />
          )}
        </div>
      </div>
    </>
  );
}

export default Documents;
